import _ from "lodash";
/** External */
import React, { useState } from "react";
import { connect } from "react-redux";
import { ToggleSwitch, Button, Badge, Tooltip } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";

/** Internal */
import { graphql, compose } from "lib/apolloClient";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import withLoader from "UIComponents/GqlWithLoader";
import { getInternalToolGenericDataQuery } from "InternalTools/modules/InternalToolsQuery";
import {
  TODDLE_AI,
  TODDLE_AI_MODULES,
  CURRICULUM_PROGRAM,
} from "../../../modules/OrgAddOnContants";
import { updateAddOnStatus } from "../../../modules/OrgAddOnHelpers";

/** Relative */
import classes from "./CurriculumProgramList.scss";
import StaffList from "./../StaffList";

const CurriculumProgramList = ({
  orgId,
  feature,
  statusMapping,
  updateAddOnStatus,
  curriculumProgramsOfOrg,
  getInternalToolGenericDataVariables,
  onSuccessAddOnConfigChange,
}) => {
  const { t } = useI18n(["common", "ai"]);
  const [fullScreenLoader, setFullScreenLoader] = useState(false);
  const [selectedCurriculumId, setSelectedCurriculumId] = useState(null);
  const [isStaffListModalOpen, setIsStaffListModalOpen] = useState(false);
  const [
    selectedCurriculumEnabledUsers,
    setSelectedCurriculumEnabledUsers,
  ] = useState([]);

  const isStaffSelectionDisabled = _.get(
    feature,
    "isStaffSelectionDisabled",
    false
  );

  return (
    <div>
      {!_.isEmpty(curriculumProgramsOfOrg) && (
        <div className={classes.parentContainer}>
          <div className={classes.headingClasses}>{t(feature?.label)}</div>
          <div className={classes.listContainer}>
            {_.map(curriculumProgramsOfOrg, curriculumProgram => {
              const { id, label, acronym } = curriculumProgram;

              const enabledForCurrentCurriculum = _.get(
                statusMapping,
                `${feature?.id}.${id}.status`,
                false
              );

              const enabledUsers = _.get(
                statusMapping,
                `${feature?.id}.${id}.enabledUsers`,
                []
              );

              const handleCurriculumLevelToggle = async () => {
                setFullScreenLoader(true);
                await updateAddOnStatus({
                  orgId,
                  curriculumId: id,
                  addOnType: TODDLE_AI,
                  moduleId: feature?.id,
                  updateOn: CURRICULUM_PROGRAM,
                  newStatus: !enabledForCurrentCurriculum,
                  queryVariables: getInternalToolGenericDataVariables,
                });
                if (!_.isNil(onSuccessAddOnConfigChange))
                  onSuccessAddOnConfigChange();
                setFullScreenLoader(false);
              };

              const badgeVariant = enabledForCurrentCurriculum
                ? "subtle"
                : "notifications";

              const tooltipText = enabledForCurrentCurriculum
                ? t(
                    "ai:this_feature_is_enabled_for_this_curriculum_it_will_be_enabled_for_all_the_staff"
                  )
                : t("ai:select_individial_staff_members");

              return (
                <div key={id} className={classes.curriculumProgramItem}>
                  <div>
                    <div>{label}</div>
                    <div
                      className={classes.acronymTypeString}
                    >{`${acronym} - (${id})`}</div>
                  </div>
                  <div className={classes.actionItems}>
                    <div className={classes.curriculumLevel}>
                      <div className={classes.curriculumWideText}>
                        {t("ai:curriculum_wide")}
                      </div>
                      <ToggleSwitch
                        onChange={handleCurriculumLevelToggle}
                        checked={enabledForCurrentCurriculum}
                      />
                    </div>
                    {!isStaffSelectionDisabled ? (
                      <>
                        <div className={classes.orSeperator}>
                          {t("common:or_lowercase")}
                        </div>
                        <Badge
                          variant={badgeVariant}
                          value={_.size(enabledUsers)}
                          placement="topRight"
                        >
                          <Tooltip tooltip={tooltipText}>
                            <Button
                              disabled={enabledForCurrentCurriculum}
                              _legacy_variant="outlined-subtle"
                              type="outlined"
                              variant="neutral"
                              onClick={() => {
                                setSelectedCurriculumId(id);
                                setSelectedCurriculumEnabledUsers(enabledUsers);
                                setIsStaffListModalOpen(true);
                              }}
                            >
                              {t("common:select_staff")}
                            </Button>
                          </Tooltip>
                        </Badge>
                      </>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {isStaffListModalOpen ? (
        <StaffList
          orgId={orgId}
          feature={feature}
          setFullScreenLoader={setFullScreenLoader}
          selectedCurriculumId={selectedCurriculumId}
          isStaffListModalOpen={isStaffListModalOpen}
          setIsStaffListModalOpen={setIsStaffListModalOpen}
          selectedCurriculumEnabledUsers={selectedCurriculumEnabledUsers}
          onSuccessAddOnConfigChange={onSuccessAddOnConfigChange}
          getInternalToolGenericDataVariables={
            getInternalToolGenericDataVariables
          }
        />
      ) : null}
      {fullScreenLoader ? <FullScreenLoader /> : null}
    </div>
  );
};

const mapActionCreators = {
  updateAddOnStatus,
};

const mapStateToProps = (state, ownProps) => {
  const { orgId } = ownProps;

  const getInternalToolGenericDataVariables = {
    input: {
      apiType: "ADD_ON_MANAGER",
      params: {
        organizationId: orgId,
        addOnType: TODDLE_AI,
      },
    },
  };

  return {
    isData: true,
    isLoading: false,
    organizationId: orgId,
    getInternalToolGenericDataVariables,
  };
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getInternalToolGenericDataQuery, {
    name: "getInternalToolGenericData",
    options: ({ getInternalToolGenericDataVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: getInternalToolGenericDataVariables,
    }),
    props: ({
      getInternalToolGenericData,
      ownProps: { isData, isLoading },
    }) => {
      const queryData = _.get(
        getInternalToolGenericData,
        "platform.internalToolsGenericQuery",
        {}
      );

      const curriculumPrograms = _.get(
        queryData,
        "data.curriculumPrograms",
        []
      );

      const statusMapping = _.reduce(
        TODDLE_AI_MODULES,
        (moduleWiseStatus, module) => {
          const moduleId = module?.id;
          const curriculumStatus = _.reduce(
            curriculumPrograms,
            (curriculumWiseStatus, curriculumProgram) => {
              const { id, status, enabledUsers } = curriculumProgram;
              return {
                ...curriculumWiseStatus,
                [id]: {
                  status: _.get(status, moduleId, false),
                  enabledUsers: _.get(enabledUsers, moduleId, []),
                },
              };
            },
            {}
          );
          return {
            ...moduleWiseStatus,
            [moduleId]: curriculumStatus,
          };
        },
        {}
      );

      return {
        statusMapping,
        isData: !_.isEmpty(queryData) && isData,
        isLoading:
          _.includes([1, 2], getInternalToolGenericData["networkStatus"]) ||
          isLoading,
      };
    },
  }),
  withLoader
)(CurriculumProgramList);
